import React, { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../contexts/AuthContext';
import { redirectBasedOnPermission } from '../../../utils/methods';
import AuthService from '../../../services/auth.service';
import { Login as LoginInterface } from '../../../interfaces/auth.interface';
import { Validation } from '../../../interfaces/common';
import { Modules, NonAuthRoutes } from '../../../enums';
import { validateLoginForm } from './validations';
import Header from './components/Header';
import Form from './components/Form';
import TwoFactorHeader from './components/TwoFactorHeader';
import TwoFactorForm from './components/TwoFactorForm';

const Login: React.FC = () => {
  const { setLoggedInUser, setToken, setPermissions, setIsSystemAdmin } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<LoginInterface>({
    phoneNumber: ''
  });

  const [validation, setValidation] = useState<Validation>({ validate: true, fields: [] });
  const [loading, setLoading] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loggedInStates, setLoggedInStates] = useState<any>({
    user: null,
    token: null,
    permissions: null,
    isSystemUser: null,
  });
  
  const handleInputChange = useCallback(
    (event: any, name: string) => {
      setFormData((prev) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setFormData]
  );

  const handleInputBlur = useCallback(
    (event: any, name: string) => {
      let newValidation = validateLoginForm(
        {
          ...formData,
          [name]: event.target.value.trim(),
        },
        name
      );
      setValidation(newValidation);
    },
    [formData]
  );

  const handleSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event && event.preventDefault();

      if (loading) return;

      const newValidation = validateLoginForm(formData);
      setValidation(newValidation);

      if (!newValidation.validate) return;

      setLoading(true);

      try {
        const data: any = await AuthService.login({
          phoneNumber: formData.phoneNumber.trim(),
        });

        if (data.success !== false && data.status !== 'error') {
          const user = data?.user ?? {};
          const isSystemUser = user?.is_system_user || user?.is_system_admin === '1' || data?.is_system_admin === '1';
          const userPermissions = data?.user?.permissions ?? data?.permissions ?? null;

          setLoggedInStates({
            user,
            token: data.token ?? '',
            permissions: userPermissions,
            isSystemUser: isSystemUser ? 'true' : 'false',
          });
          setLoggedIn(true);

          setLoading(false);
        }
      } catch (err: any) {
        setLoading(false);
      }
    },
    [formData, setLoggedInUser, setToken, setPermissions, setIsSystemAdmin, navigate, loading]
  );

  const handleKeyDown = useCallback(
    (e: any) => {
      if (loading) return;
      if (e.key === 'Enter') handleSubmit();
    },
    [handleSubmit, loading]
  );

  const handleForgotPassword = () => {
    navigate(Modules.auth + NonAuthRoutes.forgotPassword);
  };

  const handleTwoFactorSubmit = async (otp: string) => {
    setLoading(true);
    try {
      const data: any = await AuthService.verifyOtp({
        phone: formData.phoneNumber.trim(),
        otp: otp
      });

      if (data.success !== false && data.status !== 'error') {
        const user = data?.user ?? {};
        const isSystemUser = user?.is_system_user || user?.is_system_admin === '1' || data?.is_system_admin === '1';
        const userPermissions = data?.user?.permissions ?? data?.permissions ?? null;

        setLoggedInUser(user);
        setToken(data.token);
        setPermissions(userPermissions);
        setIsSystemAdmin(isSystemUser ? 'true' : 'false');

        const redirectPath = redirectBasedOnPermission(
          isSystemUser,
          userPermissions
        );
        const forbiddenPath = Modules.error + NonAuthRoutes.forbidden;
        navigate(redirectPath || forbiddenPath);
      }
    } catch (err) {
      // Error handled by interceptor or just stop loading
    }
    setLoading(false);
  }

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      await AuthService.login({
        phoneNumber: formData.phoneNumber.trim(),
      });
      // Optionally show success toast for resend
    } catch (err) {
      // Error handled
    }
    setLoading(false);
  };
  
  return (
    <div className="d-flex flex-column">
      {loggedIn ? <TwoFactorHeader phoneNumber={formData.phoneNumber} onChangeNumber={() => setLoggedIn(false)} /> : <Header />}
      {loggedIn 
        ? 
        <TwoFactorForm
          onSubmit={handleTwoFactorSubmit}
          phoneNumber={formData.phoneNumber}
          onResend={handleResendOtp}
          loading={loading}
        />
        : 
        <Form 
          formData={formData}
          validation={validation}
          loading={loading}
          onInputChange={handleInputChange}
          onInputBlur={handleInputBlur}
          onSubmit={handleSubmit}
          onKeyDown={handleKeyDown}
          onForgotPassword={handleForgotPassword}
        />
      }
    </div>
  );
};

export default Login;
